import type { RouteRecordRaw } from 'vue-router';
import NavigationRailView from '@/services/router/views/NavigationRailView.vue';
import WrapperView from '@/shared/views/WrapperView.vue';
import AssistentDashboardView from '@/ai/assistent/views/AssistentDashboardView.vue';
import AssistentEditView from '@/ai/assistent/views/AssistentEditView.vue';
import AssistentDisplayView from '@/ai/assistent/views/AssistentEdit/AssistentDisplayView.vue';
import AssistentPersonalityView from '@/ai/assistent/views/AssistentEdit/AssistentPersonalityView.vue';
import AssistentFunctionalityView from '@/ai/assistent/views/AssistentEdit/AssistentFunctionalityView.vue';
import AssistentKnowledgeView from '@/ai/assistent/views/AssistentEdit/AssistentKnowledgeView.vue';
import AssistentInstructionView from '@/ai/assistent/views/AssistentEdit/AssistentInstructionView.vue';
import AssistentQuickOptionsView from '@/ai/assistent/views/AssistentEdit/AssistentQuickOptionsView.vue';
import AssistentChatConnectView from '@/ai/assistent/views/AssistentEdit/AssistentChatConnectView.vue';
import AssistentChatConnectGoalView from '@/ai/assistent/views/AssistentEdit/AssistentChatConnectGoalView.vue';
import AssistentChatConnectSystemGoalView from '@/ai/assistent/views/AssistentEdit/AssistentChatConnectSystemGoalView.vue';
import AssistentEmbedCodeView from '@/ai/assistent/views/AssistentEdit/AssistentEmbedCodeView.vue';
import AssistentInboxSkeletonView from '@/ai/assistent/views/AssistentInboxSkeletonView.vue';
import AssistentInboxView from '@/ai/assistent/views/AssistentInboxView.vue';
import InboxFolder from '@/ai/assistent/types/InboxFolder';
import AssistentReportView from '@/ai/assistent/views/AssistentReportView.vue';
import AssistentHandOverView from '@/ai/assistent/views/AssistentEdit/AssistentHandOverView.vue';

const routes: RouteRecordRaw[] = [
    {
        path: '/ai-medewerkers',
        name: 'assistent',
        components: {
            default: WrapperView,
            rail: NavigationRailView,
        },
        redirect: { name: 'assistent-dashboard' },
        children: [
            {
                path: '',
                name: 'assistent-dashboard',
                components: {
                    default: AssistentDashboardView,
                },
            },
            {
                path: ':assistentId/report',
                name: 'assistent-report',
                components: {
                    default: AssistentReportView,
                },
            },
            {
                path: ':assistentId',
                name: 'assistent-edit',
                redirect: { name: 'assistent-chat-edit-personality' },
                props: true,
                components: {
                    default: AssistentEditView,
                },
                meta: {
                    noPadding: true,
                    allowedRoles: [
                        'company-manager',
                    ],
                },
                children: [
                    {
                        path: 'personality',
                        name: 'assistent-chat-edit-personality',
                        props: true,
                        components: {
                            default: AssistentPersonalityView,
                        },
                    },
                    {
                        path: 'advanced',
                        name: 'assistent-chat-edit-advanced',
                        redirect: { name: 'assistent-chat-edit-functionality' },
                        children: [
                            {
                                path: 'functionality',
                                name: 'assistent-chat-edit-functionality',
                                props: true,
                                components: {
                                    default: AssistentFunctionalityView,
                                },
                            },
                            {
                                path: 'knowledge',
                                name: 'assistent-chat-edit-knowledge',
                                props: true,
                                components: {
                                    default: AssistentKnowledgeView,
                                },
                            },
                            {
                                path: 'display',
                                name: 'assistent-chat-edit-display',
                                props: true,
                                components: {
                                    default: AssistentDisplayView,
                                },
                            },
                            {
                                path: 'instruction',
                                name: 'assistent-chat-edit-instruction',
                                components: {
                                    default: AssistentInstructionView,
                                },
                            },
                            {
                                path: 'handover',
                                name: 'assistent-chat-edit-handover',
                                props: true,
                                components: {
                                    default: AssistentHandOverView,
                                },
                            },
                            {
                                path: 'options',
                                name: 'assistent-chat-edit-options',
                                components: {
                                    default: AssistentQuickOptionsView,
                                },
                            },
                            {
                                path: 'embed-code',
                                name: 'assistent-chat-edit-embed-code',
                                props: true,
                                components: {
                                    default: AssistentEmbedCodeView,
                                },
                            },
                            {
                                path: 'chat-connect',
                                name: 'assistent-chat-edit-chat-connect',
                                props: true,
                                components: {
                                    default: AssistentChatConnectView,
                                },
                                children: [
                                    {
                                        path: 'goals/:goal',
                                        name: 'assistent-chat-edit-chat-connect-goal',
                                        props: true,
                                        components: {
                                            default: AssistentChatConnectGoalView,
                                        },
                                    },
                                    {
                                        path: 'system-goals/:systemGoal',
                                        name: 'assistent-chat-edit-chat-connect-system-goals',
                                        props: true,
                                        components: {
                                            default: AssistentChatConnectSystemGoalView,
                                        },
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: ':assistentId/chats/:page?/',
                name: 'assistent-chat-list',
                redirect: (to) => ({
                    name: 'assistent-detail-folder',
                    params: {
                        assistentId: to.params.assistentId,
                        inbox: InboxFolder.CONVERSATIONS.routeSlug,
                    },
                }),
                children: [
                    {
                        path: ':chatId',
                        name: 'assistent-chat-detail',
                        redirect: (to) => ({
                            name: 'assistent-detail-folder',
                            params: {
                                assistentId: to.params.assistentId,
                                inbox: InboxFolder.CONVERSATIONS.routeSlug,
                                id: to.params.chatId,
                            },
                        }),
                    },
                ],
            },
        ],
    },
    {
        path: '/berichten',
        name: 'assistent-inbox',
        components: {
            default: WrapperView,
            rail: NavigationRailView,
        },
        redirect: { name: 'assistent-inbox-skeleton' },
        children: [
            {
                path: '',
                name: 'assistent-inbox-skeleton',
                components: {
                    default: AssistentInboxSkeletonView,
                },
            },
            {
                path: ':assistentId',
                name: 'assistent-detail',
                redirect: (to) => ({
                    name: 'assistent-detail-folder',
                    params: {
                        assistentId: to.params.assistentId,
                        inbox: InboxFolder.CONVERSATIONS.routeSlug,
                    },
                }),
                children: [
                    {
                        path: ':inbox/:id?',
                        name: 'assistent-detail-folder',
                        props: true,
                        components: {
                            default: AssistentInboxView,
                        },
                    },
                ],
            },
        ],
        meta: {
            noPadding: true,
        },
    },
];

export default routes;
